import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, TextField, Typography } from '@mui/material';
import { Check, KeyboardArrowDown, Search } from '@mui/icons-material';
import { CreatableSelect, InfoTip } from '../../ui';
import { tokens } from '../../../theme/tokens';
import { normalizeMotzuSlug, taxonomyLabel, taxonomyShortLabel, UNMAPPED_CATEGORY_UNSET_SHORT } from '../mappingModel';
import CategorySelect from './CategorySelect';
import CategorySlugLabel from '../CategorySlugLabel';
import { LinkAction, SegmentedControl, StepCircle, v2CardSx, v2Mono } from './v2Ui';

export type MapFilter = 'all' | 'unmapped' | 'mapped' | 'override' | 'suggested';

// The table lives in a horizontal scroller (minWidth below), so it keeps its columns at every width.
const COLUMNS = { xs: '36px minmax(0,0.9fr) minmax(0,1.1fr) minmax(0,1.1fr) 96px' };

interface CategoryMapStepProps {
  n: number;
  slugs: string[];
  /** Flag `lens_focal_range`: hint under the lens focal-range rows. */
  lensFocalRange?: boolean;
  categoryMap: Record<string, string>;
  /** The store's own Shopify category per slug (an override of Motzu's default). */
  taxonomyMap: Record<string, string>;
  /** Product type proposals from the last discovery, by slug. */
  suggestions: Record<string, string>;
  /** True once a discovery ran, which is when the Suggested filter exists. */
  discovered: boolean;
  filter: MapFilter;
  onFilter: (filter: MapFilter) => void;
  typeOptions: string[];
  taxonomyOptions: Array<{ gid: string; label: string }>;
  /** The Shopify category a slug gets with no override, and where that default comes from. */
  defaultFor: (slug: string) => {
    gid: string | null;
    builtIn: boolean;
    /** The store's "Don't set" fallback applies (flag `shopify_unmapped_category_unset`): no category is sent. */
    unset?: boolean;
  };
  fallbackText: (slug: string) => string;
  /** Product type owner is Never touch: the column is kept but not written. */
  typeNeverTouch: boolean;
  /** The old layout switched the category map off; the rows are not applied until the store says so. */
  mapSwitchedOff: boolean;
  onApplyMap: () => void;
  onSetType: (slug: string, value: string) => void;
  onSetCategory: (slug: string, gid: string) => void;
  onBulk: (slugs: string[], type: string, gid: string) => void;
  onAcceptSuggestion: (slug: string) => void;
  onResetCategories: () => void;
  newSlugOptions: Array<string | { value: string; hint?: string }>;
  onAddSlug: (slug: string) => void;
}

const HEADERS: Array<{ label: string; tip?: string }> = [
  { label: 'Motzu Classification' },
  { label: 'Shopify Product Type', tip: 'The Shopify Product Type products with this Motzu Classification get. Leave it empty to use the Unmatched Product Rules.' },
  { label: 'Shopify Product Category', tip: 'The Shopify Product Category sent for this Motzu Classification. Default uses Motzu\'s built-in match; pick another to override it.' },
  { label: 'Type from', tip: 'Which step decided this row\'s type: Override means your row here, Unmatched means the Unmatched Product Rules decide it because the row has no type.' },
];

/** Step 3, Motzu Classification Overrides: Motzu Classification -> Shopify Product Type and Shopify Product Category. */
const CategoryMapStep: React.FC<CategoryMapStepProps> = ({
  n,
  slugs,
  lensFocalRange = false,
  categoryMap,
  taxonomyMap,
  suggestions,
  discovered,
  filter,
  onFilter,
  typeOptions,
  taxonomyOptions,
  defaultFor,
  fallbackText,
  typeNeverTouch,
  mapSwitchedOff,
  onApplyMap,
  onSetType,
  onSetCategory,
  onBulk,
  onAcceptSuggestion,
  onResetCategories,
  newSlugOptions,
  onAddSlug,
}) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [newSlug, setNewSlug] = useState('');

  const hasType = (slug: string) => Boolean(categoryMap[slug]?.trim());
  const isOverride = (slug: string) => Boolean(taxonomyMap[slug]);
  const suggestionFor = (slug: string) => (suggestions[slug] && suggestions[slug] !== categoryMap[slug] ? suggestions[slug] : '');

  const total = slugs.length;
  const mapped = slugs.filter(hasType).length;
  const overrides = slugs.filter(isOverride).length;
  const suggested = slugs.filter((slug) => suggestionFor(slug)).length;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return slugs.filter((slug) => {
      if (needle && !slug.includes(needle)) return false;
      if (filter === 'mapped') return hasType(slug);
      if (filter === 'unmapped') return !hasType(slug);
      if (filter === 'override') return isOverride(slug);
      if (filter === 'suggested') return Boolean(suggestionFor(slug));
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugs, query, filter, categoryMap, taxonomyMap, suggestions]);

  const selectedVisible = visible.filter((slug) => selected.has(slug));
  const allSelected = visible.length > 0 && selectedVisible.length === visible.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;

  const toggle = (slug: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const applyBulk = () => {
    if ((!bulkType.trim() && !bulkCategory) || selectedVisible.length === 0) return;
    onBulk(selectedVisible, bulkType.trim(), bulkCategory);
    setSelected(new Set());
    setBulkType('');
    setBulkCategory('');
  };

  const addSlug = () => {
    const slug = normalizeMotzuSlug(newSlug);
    if (!slug) return;
    onAddSlug(slug);
    setNewSlug('');
    setQuery('');
  };

  const filters: Array<{ value: MapFilter; label: string; count: number; countTone?: 'caution' | 'info' }> = [
    { value: 'all', label: 'All', count: total },
    { value: 'unmapped', label: 'No type', count: total - mapped, countTone: 'caution' },
    { value: 'mapped', label: 'Has type', count: mapped },
    { value: 'override', label: 'Category override', count: overrides },
    ...(discovered ? [{ value: 'suggested' as const, label: 'Suggested', count: suggested, countTone: 'info' as const }] : []),
  ];

  return (
    <Box sx={{ ...v2CardSx, overflow: 'hidden' }}>
      <Box sx={{ px: 2.25, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <StepCircle n={n} tone="info" />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Motzu Classification Overrides</Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: '3px' }}>
            When to use: to rename or regroup Motzu&apos;s classifications into your store&apos;s own words, one row per Motzu Classification. A row applies only when your Store Taxonomy and Name Rules did not decide the product.
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
          {mapped} of {total} have a Shopify Product Type · {overrides} Shopify Product Category overrides
        </Typography>
        {overrides > 0 && (
          <Box sx={{ ml: 'auto' }}>
            <LinkAction onClick={onResetCategories}>Reset categories to defaults</LinkAction>
          </Box>
        )}
      </Box>
      <Box sx={{ mx: 2.25, mb: 1.5, height: 4, borderRadius: '2px', bgcolor: tokens.surface.tint, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: total ? `${(mapped / total) * 100}%` : 0, bgcolor: tokens.accent.main, transition: 'width 250ms ease' }} />
      </Box>

      {mapSwitchedOff && (
        <Box
          sx={{
            mx: 2.25,
            mb: 1.5,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            borderRadius: '9px',
            bgcolor: tokens.surface.tint,
            fontSize: 12.5,
            color: tokens.ink.bodyAlt,
          }}
        >
          <Box sx={{ flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Motzu Classification Overrides were switched off in the previous layout, so these rows are not applied
            <InfoTip
              label="About Apply overrides"
              title="The layout you had before had its Motzu Classification Overrides switched off, so these rows are saved but not used. Apply overrides switches them back on, and the rows take effect after you Save and the next sync."
            />
          </Box>
          <Button size="small" variant="outlined" onClick={onApplyMap} sx={{ fontWeight: 600 }}>Apply overrides</Button>
        </Box>
      )}
      {typeNeverTouch && (
        <Box sx={{ mx: 2.25, mb: 1.5, px: 1.5, py: 1, borderRadius: '9px', bgcolor: tokens.surface.tint, fontSize: 12.5, color: tokens.ink.bodyAlt }}>
          Shopify Product Type is set to Never touch, so this column is kept but not written to Shopify.
        </Box>
      )}

      <Box sx={{ px: 2.25, pb: 1.5, display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
        <SegmentedControl
          kind="filter"
          aria-label="Motzu Classification Overrides filter"
          options={filters}
          value={filter}
          onChange={onFilter}
        />
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a Motzu Classification"
          inputProps={{ 'aria-label': 'Find a Motzu Classification' }}
          slotProps={{ input: { startAdornment: <Search sx={{ fontSize: 16, color: tokens.ink.faint, mr: 0.75 }} /> } }}
          sx={{ flex: '1 1 180px', '& .MuiInputBase-root': { height: 34 } }}
        />
      </Box>

      {selectedVisible.length > 0 && (
        <Box
          sx={{
            mx: 2.25,
            mb: 1.25,
            px: 1.5,
            py: 1,
            borderRadius: '10px',
            bgcolor: tokens.surface.header,
            color: tokens.ink.inverse,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{selectedVisible.length} selected</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.onDark }}>Set Shopify Product Type</Typography>
          <Box
            sx={{ flex: '1 1 160px', minWidth: 140, '& .MuiInputBase-root': { bgcolor: tokens.surface.card } }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyBulk();
              }
            }}
          >
            <CreatableSelect
              label=""
              aria-label="Set Shopify Product Type for selected Motzu Classifications"
              value={bulkType}
              options={typeOptions}
              onChange={setBulkType}
              placeholder="Shopify Product Type"
            />
          </Box>
          <Typography sx={{ fontSize: 13, color: tokens.ink.onDark }}>Shopify Product Category</Typography>
          <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
            <CategorySelect
              aria-label="Set Shopify Product Category for selected Motzu Classifications"
              value={bulkCategory}
              options={taxonomyOptions}
              emptyLabel="Keep as is"
              onChange={setBulkCategory}
              sx={{ height: 34 }}
            />
          </Box>
          <Button
            size="small"
            variant="contained"
            disabled={!bulkType.trim() && !bulkCategory}
            onClick={applyBulk}
            sx={{ fontWeight: 700 }}
          >
            Apply
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())} sx={{ color: tokens.ink.onDark, fontWeight: 600 }}>
            Clear
          </Button>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 760 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, px: 1.5, minHeight: 34, py: 0.5, alignItems: 'center', columnGap: 1, bgcolor: tokens.surface.sunken }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (allSelected) visible.forEach((slug) => next.delete(slug));
                  else visible.forEach((slug) => next.add(slug));
                  return next;
                })
              }
              inputProps={{ 'aria-label': 'Select visible Motzu Classifications' }}
            />
            {HEADERS.map(({ label, tip }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                  {label}
                </Typography>
                {tip && <InfoTip label={`About ${label}`} title={tip} />}
              </Box>
            ))}
          </Box>
          {/* No scroll area of its own: Classification is a page, so the map grows with it (MOT-252). */}
          <Box data-testid="category-map-rows">
            {visible.length === 0 && (
              <Typography sx={{ px: 2, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>
                {query.trim() ? `No category matches “${query.trim()}”.` : 'Nothing here.'}
              </Typography>
            )}
            {visible.map((slug) => {
              const type = categoryMap[slug] || '';
              const suggestion = suggestionFor(slug);
              const override = taxonomyMap[slug] || '';
              const def = defaultFor(slug);
              const shownGid = override || def.gid || '';
              const defaultName = def.unset
                ? UNMAPPED_CATEGORY_UNSET_SHORT
                : def.gid ? taxonomyShortLabel(taxonomyLabel(taxonomyOptions, def.gid)) : 'none';
              const leaf = shownGid
                ? taxonomyShortLabel(taxonomyLabel(taxonomyOptions, shownGid))
                : def.unset ? UNMAPPED_CATEGORY_UNSET_SHORT : '—';
              return (
                <Box
                  key={slug}
                  data-testid={`map-row-${slug}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLUMNS,
                    columnGap: 1,
                    alignItems: 'center',
                    minHeight: 56,
                    px: 1.5,
                    py: 0.75,
                    borderTop: '1px solid',
                    borderColor: tokens.line.hairline,
                    bgcolor: selected.has(slug) ? tokens.signal.info.row : 'transparent',
                  }}
                >
                  <Checkbox size="small" checked={selected.has(slug)} onChange={() => toggle(slug)} inputProps={{ 'aria-label': `Select ${slug}` }} />
                  <CategorySlugLabel slug={slug} monoSx={v2Mono} lensFocalRange={lensFocalRange} />
                  <Box sx={{ minWidth: 0, pr: 0.5, opacity: typeNeverTouch ? 0.5 : 1 }}>
                    <CreatableSelect
                      label=""
                      aria-label={`Shopify Product Type for ${slug}`}
                      value={type}
                      options={typeOptions}
                      onChange={(value) => onSetType(slug, value)}
                      placeholder={`Falls back to ${fallbackText(slug)}`}
                      sx={type ? undefined : { '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.signal.caution.border } }}
                    />
                    {suggestion && (
                      <Button
                        size="small"
                        startIcon={<Check sx={{ fontSize: 14 }} />}
                        onClick={() => onAcceptSuggestion(slug)}
                        aria-label={`Accept suggestion ${suggestion} for ${slug}`}
                        sx={{
                          mt: 0.5,
                          height: 24,
                          border: '1px dashed',
                          borderColor: tokens.accent.border,
                          bgcolor: tokens.accent.tint,
                          color: tokens.accent.hover,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {suggestion}
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ position: 'relative', minWidth: 0, pr: 0.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: 44,
                        px: 1.25,
                        border: '1px solid',
                        borderColor: tokens.line.default,
                        borderRadius: '8px',
                        bgcolor: tokens.surface.card,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: override ? 600 : 500, color: override ? tokens.ink.primary : tokens.ink.bodyAlt, lineHeight: 1.3 }}>
                          {leaf}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 11, color: tokens.ink.faint, lineHeight: 1.3 }}>
                          {override
                            ? `Override · ${taxonomyLabel(taxonomyOptions, override).replace(/\s*>\s*/g, ' › ')}`
                            : def.builtIn ? 'Default' : 'Default (nothing matches)'}
                        </Typography>
                      </Box>
                      <KeyboardArrowDown sx={{ fontSize: 16, color: tokens.ink.faint, flex: '0 0 auto' }} />
                    </Box>
                    <Box
                      component="select"
                      aria-label={`Shopify Product Category for ${slug}`}
                      value={override}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onSetCategory(slug, event.target.value)}
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                    >
                      <option value="">{`Default · ${defaultName}`}</option>
                      {override && !taxonomyOptions.some((option) => option.gid === override) && (
                        <option value={override}>{taxonomyLabel(taxonomyOptions, override)}</option>
                      )}
                      {taxonomyOptions.map((option) => (
                        <option key={option.gid} value={option.gid}>{option.label}</option>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      component="span"
                      sx={{
                        px: 0.875,
                        py: 0.25,
                        borderRadius: `${tokens.radius.chip}px`,
                        fontSize: 11.5,
                        fontWeight: 700,
                        bgcolor: type ? tokens.signal.positive.tint : tokens.signal.caution.tint,
                        color: type ? tokens.signal.positive.ink : tokens.signal.caution.ink,
                      }}
                    >
                      {type ? 'Override' : 'Unmatched'}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 2, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
        <Box sx={{ flex: 1, minWidth: 0, '& input': v2Mono }}>
          <CreatableSelect
            label=""
            aria-label="Add a Motzu Classification not listed"
            value={newSlug}
            options={newSlugOptions}
            onChange={setNewSlug}
            placeholder="Add a Motzu Classification not listed, e.g. gimbal"
          />
        </Box>
        <Button size="small" variant="outlined" disabled={!normalizeMotzuSlug(newSlug)} onClick={addSlug} sx={{ fontWeight: 600 }}>
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default CategoryMapStep;
